var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Object3D } from "three";
import { PointCloud } from "../index";
import { assert } from "../../../utils";
import { Batch3DModel, Instanced3DModel } from "..";
import { OptionsManager } from "../../../options";
export class Composite extends Object3D {
    constructor() {
        super(...arguments);
        this.pointClouds = [];
        this.batchModels = [];
        this.instancedModels = [];
        this.composites = [];
        this.tileCost = 1;
    }
    static create(tile, data, styleDesc, materialRegistry, pickingManager, owner) {
        return __awaiter(this, void 0, void 0, function* () {
            // View of array buffer for reading
            const view = data;
            // Check if the file header is valid ("pnts"). If not then we aren't reading a pnts file and should throw
            assert(Composite.isValidHeader(view));
            // We only support version 1, check the field in the header
            const version = view.getUint32(4, true);
            assert(version === 1);
            // const cmptByteLength = view.getUint32(8, true);
            const tilesLength = view.getUint32(12, true);
            const object = new Composite();
            let remaining = new DataView(view.buffer, view.byteOffset + 16);
            for (let i = 0; i < tilesLength; i += 1) {
                // The size, in bytes, of the tile we just read
                let byteLength;
                // The object the tile will be loaded to
                let tileObject;
                // Switch over the possible header data values. If we identify a known 3D tiles format header we read the
                // byte length off, make a view over the region that contains the tile and load it.
                if (PointCloud.isValidHeader(remaining)) {
                    byteLength = remaining.getUint32(8, true);
                    const pc = yield PointCloud.create(tile, new DataView(remaining.buffer, remaining.byteOffset, byteLength), styleDesc, materialRegistry, pickingManager, owner);
                    object.pointClouds.push(pc);
                    tileObject = pc;
                }
                else if (Batch3DModel.isValidHeader(remaining)) {
                    byteLength = remaining.getUint32(8, true);
                    const bm = yield Batch3DModel.create(tile, new DataView(remaining.buffer, remaining.byteOffset, byteLength), styleDesc, materialRegistry, pickingManager, owner);
                    object.batchModels.push(bm);
                    tileObject = bm;
                }
                else if (Instanced3DModel.isValidHeader(remaining)) {
                    byteLength = remaining.getUint32(8, true);
                    const im = yield Instanced3DModel.create(tile, new DataView(remaining.buffer, remaining.byteOffset, byteLength), styleDesc, materialRegistry, pickingManager, owner);
                    object.instancedModels.push(im);
                    tileObject = im;
                }
                else if (Composite.isValidHeader(remaining)) {
                    byteLength = remaining.getUint32(8, true);
                    const c = yield Composite.create(tile, new DataView(remaining.buffer, remaining.byteOffset, byteLength), styleDesc, materialRegistry, pickingManager, owner);
                    object.composites.push(c);
                    tileObject = c;
                }
                else {
                    throw new Error("Unknown tile format");
                }
                // Add the tile to our Composite object
                object.add(tileObject);
                // Skip over the bytes we just read to get to the start of the next tile for the next loop iteration
                remaining = new DataView(remaining.buffer, remaining.byteOffset + byteLength);
            }
            const pcCost = OptionsManager.POINT_CLOUD_TILE_WEIGHT * object.pointClouds.length;
            const bmCost = OptionsManager.BATCH_MODEL_TILE_WEIGHT * object.batchModels.length;
            const imCost = OptionsManager.INSTANCED_MODEL_TILE_WEIGHT * object.instancedModels.length;
            const cCost = object.composites.map((v) => v.tileCost).reduce((p, c) => p + c, 0);
            object.tileCost = pcCost + bmCost + imCost + cCost;
            return object;
        });
    }
    static isValidHeader(view) {
        if (view.getUint8(0) !== 99)
            return false; // c
        if (view.getUint8(1) !== 109)
            return false; // m
        if (view.getUint8(2) !== 112)
            return false; // p
        if (view.getUint8(3) !== 116)
            return false; // t
        return true;
    }
    evict() {
        for (const i of this.pointClouds) {
            i.evict();
        }
        for (const i of this.batchModels) {
            i.evict();
        }
        for (const i of this.instancedModels) {
            i.evict();
        }
        for (const i of this.composites) {
            i.evict();
        }
    }
}
